import React from 'react';
import { Card, CardContent, Box, Typography, IconButton, SvgIconProps, useTheme } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';

interface DataCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ComponentType<SvgIconProps>;
  iconColor?: string;
  value?: string | number;
  valueLabel?: string;
  onClick?: () => void;
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  children?: React.ReactNode;
}

export const DataCard: React.FC<DataCardProps> = ({
  title,
  subtitle,
  icon: Icon,
  iconColor,
  value,
  valueLabel,
  onClick,
  onMenuClick,
  children,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              boxShadow: 4,
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {Icon && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: iconColor ? `${iconColor}20` : theme.palette.primary.light + '20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  sx={{
                    fontSize: 24,
                    color: iconColor || theme.palette.primary.main,
                  }}
                />
              </Box>
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
          {onMenuClick && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick(e);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {(value !== undefined || valueLabel) && (
          <Box sx={{ mt: 2 }}>
            {value !== undefined && (
              <Typography variant="h4" fontWeight={600}>
                {value}
              </Typography>
            )}
            {valueLabel && (
              <Typography variant="body2" color="text.secondary">
                {valueLabel}
              </Typography>
            )}
          </Box>
        )}

        {children && <Box sx={{ mt: 2, flex: 1 }}>{children}</Box>}
      </CardContent>
    </Card>
  );
};

export default DataCard;
